import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  TextInput,
  FlatList,
  Image,
  KeyboardAvoidingView,
  Platform,
  Alert,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { useMessages, startConversation } from '../../hooks/useChat';
// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');

const defaultLogo = { uri: 'https://via.placeholder.com/100/0e4e96/FFFFFF?text=%20' };

function formatTimeShort(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function ChatDetailScreen({ navigation, route }) {
  // ── Profile integration ──
  const profileCtx = useProfile();
  const { conversation: routeConv } = route.params || {};
  const [conversation, setConversation] = useState(routeConv || null);
  const [conversationId, setConversationId] = useState(
    routeConv && /^\d+$/.test(String(routeConv.id || '')) ? parseInt(routeConv.id, 10) : null
  );
  const [bootstrapError, setBootstrapError] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const flatListRef = useRef(null);

  // If the navigator was opened with otherRole+otherUserId but no conversation id,
  // call /conversations/start to find-or-create the thread.
  useEffect(() => {
    if (conversationId || !routeConv) return;
    if (!routeConv.otherRole || !routeConv.otherUserId) {
      setBootstrapError('Missing recipient. Open this conversation from the inbox.');
      return;
    }
    (async () => {
      try {
        const r = await startConversation({
          recipient_role:    routeConv.otherRole,
          recipient_user_id: routeConv.otherUserId,
          company_id:        routeConv.companyId,
        });
        setConversationId(r.conversation_id);
        setConversation(prev => ({ ...(prev || {}), id: r.conversation_id }));
      } catch (e) {
        setBootstrapError(e.message || 'Could not open conversation');
      }
    })();
  }, [conversationId, routeConv]);

  const { messages, loading, error, send, markRead, refresh } =
    useMessages(conversationId, { pollMs: 3000 });

  // Re-mark-read on every focus.
  useFocusEffect(useCallback(() => {
    if (conversationId) markRead();
  }, [conversationId, markRead]));

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (flatListRef.current && messages.length > 0) {
      setTimeout(() => {
        try { flatListRef.current.scrollToEnd({ animated: true }); } catch {}
      }, 100);
    }
  }, [messages.length]);

  const sendMessage = async () => {
    const body = message.trim();
    if (!body || sending || !conversationId) return;
    setSending(true);
    setMessage('');
    try {
      await send(body);
    } catch (e) {
      Alert.alert('Send failed', e.message || 'Could not send your message — please try again.');
    } finally {
      setSending(false);
    }
  };

  // The user is "me" if the message's sender_role matches my own.
  // The hook tags optimistic messages with sender_role='__me'.
  const myRole = profileCtx?.profile?.user_type || 'professional';
  const isMine = (m) => m.sender_role === '__me' || m.sender_role === myRole;

  const renderMessage = ({ item }) => {
    const mine = isMine(item);
    return (
      <View style={[
        styles.messageContainer,
        mine ? styles.userMessageContainer : styles.companyMessageContainer,
      ]}>
        <View style={[
          styles.messageBubble,
          mine ? styles.userMessageBubble : styles.companyMessageBubble,
          item._failed && { opacity: 0.6, borderColor: '#EF4444', borderWidth: 1 },
        ]}>
          <Text style={[
            styles.messageText,
            mine ? styles.userMessageText : styles.companyMessageText,
          ]}>
            {item.body}
          </Text>
          <Text style={[
            styles.messageTimestamp,
            mine ? styles.userTimestamp : styles.companyTimestamp,
          ]}>
            {formatTimeShort(item.created_at)}
            {item._pending ? ' • sending…' : ''}
            {item._failed  ? ' • failed' : ''}
          </Text>
        </View>
      </View>
    );
  };

  const headerName     = conversation?.company || 'Conversation';
  const headerSub      = conversation?.position || '';
  const headerLogoSrc  = conversation?.logo || defaultLogo;

  const showEmpty = !loading && !bootstrapError && messages.length === 0 && conversationId;

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="chevron-back" size={24} color="#0e4e96" />
        </TouchableOpacity>

        <View style={styles.headerInfo}>
          <Image source={headerLogoSrc} style={styles.companyLogo} />
          <View style={styles.headerText}>
            <Text style={styles.companyName} numberOfLines={1}>{headerName}</Text>
            {headerSub ? <Text style={styles.positionName} numberOfLines={1}>{headerSub}</Text> : null}
          </View>
        </View>

        <View style={{ width: 36 }} />
      </View>

      {/* Body */}
      <KeyboardAvoidingView
        style={styles.chatContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
      >
        {bootstrapError ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <Ionicons name="alert-circle-outline" size={48} color="#EF4444" />
            <Text style={{ marginTop: 12, color: '#EF4444', textAlign: 'center' }}>{bootstrapError}</Text>
          </View>
        ) : loading && messages.length === 0 ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <ActivityIndicator size="large" color="#0e4e96" />
          </View>
        ) : showEmpty ? (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <Ionicons name="chatbubble-outline" size={48} color="#CBD5E1" />
            <Text style={{ marginTop: 12, color: '#64748B' }}>No messages yet — say hello.</Text>
          </View>
        ) : (
          <FlatList
            ref={flatListRef}
            data={messages}
            renderItem={renderMessage}
            keyExtractor={(item) => String(item.id)}
            style={styles.messagesList}
            contentContainerStyle={styles.messagesContent}
            showsVerticalScrollIndicator={false}
            onContentSizeChange={() => {
              try { flatListRef.current && flatListRef.current.scrollToEnd({ animated: false }); } catch {}
            }}
          />
        )}

        {/* Input */}
        <View style={styles.inputContainer}>
          <View style={styles.inputWrapper}>
            <TextInput
              style={styles.textInput}
              value={message}
              onChangeText={setMessage}
              placeholder="Type a message..."
              multiline
              maxLength={5000}
              editable={!!conversationId && !bootstrapError}
            />
            <TouchableOpacity
              onPress={sendMessage}
              style={[styles.sendButton, { opacity: (message.trim() && conversationId && !sending) ? 1 : 0.5 }]}
              disabled={!message.trim() || !conversationId || sending}
            >
              {sending ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Ionicons name="send" size={20} color="#fff" />
              )}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>

      {/* Quick Actions */}
      <View style={styles.quickActions}>
        <TouchableOpacity style={styles.quickActionButton}>
          <Ionicons name="attach-outline" size={20} color="#666" />
          <Text style={styles.quickActionText}>Attach</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={refresh}>
          <Ionicons name="refresh-outline" size={20} color="#666" />
          <Text style={styles.quickActionText}>Refresh</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton}>
          <Ionicons name="call-outline" size={20} color="#666" />
          <Text style={styles.quickActionText}>Call</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 15,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 5,
    marginRight: 10,
  },
  headerInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  companyLogo: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  headerText: {
    flex: 1,
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  positionName: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  scheduleButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
  },
  chatContainer: {
    flex: 1,
  },
  messagesList: {
    flex: 1,
    paddingHorizontal: 15,
  },
  messagesContent: {
    paddingVertical: 10,
  },
  messageContainer: {
    marginVertical: 4,
  },
  userMessageContainer: {
    alignItems: 'flex-end',
  },
  companyMessageContainer: {
    alignItems: 'flex-start',
  },
  messageBubble: {
    maxWidth: '80%',
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 20,
  },
  userMessageBubble: {
    backgroundColor: '#0e4e96',
    borderBottomRightRadius: 5,
  },
  companyMessageBubble: {
    backgroundColor: '#fff',
    borderBottomLeftRadius: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
  },
  userMessageText: {
    color: '#fff',
  },
  companyMessageText: {
    color: '#333',
  },
  messageTimestamp: {
    fontSize: 12,
    marginTop: 5,
  },
  userTimestamp: {
    color: 'rgba(255, 255, 255, 0.7)',
    textAlign: 'right',
  },
  companyTimestamp: {
    color: '#999',
  },
  inputContainer: {
    paddingHorizontal: 15,
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    backgroundColor: '#f5f5f5',
    borderRadius: 25,
    paddingHorizontal: 15,
    paddingVertical: 8,
  },
  textInput: {
    flex: 1,
    fontSize: 16,
    maxHeight: 100,
    paddingVertical: 8,
    color: '#333',
  },
  sendButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 20,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 10,
  },
  quickActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: 10,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  quickActionButton: {
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 15,
  },
  quickActionText: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
});

